import ScholarshipPresenter from '@/presenters/scholarship-presenter';
import { ref, toRaw, reactive } from 'vue';
import { Scholarship } from '@/presenters/scholarship';
import { PlatformIntegration } from '../../platform/platform-integration';
import { ScholarshipOverviewFilters } from './scholarship-overview-filters';
import { ScholarshipSearchOutcome } from '../../presenters/scholarship-search-outcome';

export default class ScholarshipOverview {
	public readonly filters = reactive(new ScholarshipOverviewFilters());

	public jumpTargetId = ref<string>();
	public titleFilter = ref<string>('');

	public scholarships = ref<readonly Scholarship[]>([]);
	public searchDescription = ref<string>();
	public isLoading = ref<boolean>(false);

	private abortController?: AbortController;

	public async mounted(): Promise<void> {
		await this.searchLatest();
	}

	public toEditScholarship(scholarship: Scholarship): void {
		scholarship = toRaw(scholarship);

		PlatformIntegration.instance.router.toModify(scholarship.id);
	}

	public toEditScholarshipById(scholarshipId: string): void {
		PlatformIntegration.instance.router.toModify(scholarshipId);
	}

	public toCreateScholarship(): void {
		PlatformIntegration.instance.router.toCreate();
	}

	public async searchLatest(): Promise<void> {
		this.filters.reset();
		const searchOutcome = await ScholarshipPresenter.searchLatest();
		this.scholarships.value = searchOutcome.scholarships;
		this.searchDescription.value = this.determineSearchDescription('most recently modified', searchOutcome);
	}

	public async search(): Promise<void> {
		const title = this.titleFilter.value?.trim() ?? '';

		this.isLoading.value = true;
		this.abortController?.abort();
		const controller = new AbortController();
		this.abortController = controller;
		const { signal } = controller;

		try {
			const searchOutcome = title.length > 0
				? await ScholarshipPresenter.searchByTitle(title, signal)
				: await ScholarshipPresenter.searchLatest(signal);

			if (signal.aborted) {
				return;
			}

			this.scholarships.value = searchOutcome.scholarships;
			const description = title.length > 0 ? `matching "${title}"` : 'most recently modified';
			this.searchDescription.value = this.determineSearchDescription(description, searchOutcome);
			this.isLoading.value = false;
		} catch (error) {
			if (signal.aborted) return;
			this.isLoading.value = false;
			throw error;
		}
	}

	private determineSearchDescription(
		searchTypeDescription: string,
		searchOutcome: ScholarshipSearchOutcome
	): string | undefined {
		let description = `${this.scholarships.value.length} ${searchTypeDescription} out of `;
		description += searchOutcome.hitsTotalExact ? searchOutcome.hitsTotal : `more than ${searchOutcome.hitsTotal}`;

		return description;
	}

	public async jump(): Promise<void> {
		if (this.jumpTargetId.value === undefined || this.jumpTargetId.value.trim().length === 0) {
			this.jumpTargetId.value = '';
		} else {
			this.toEditScholarshipById(this.jumpTargetId.value);
		}
	}
}
